/**
 * DeadStockPage.jsx
 * Clearance candidates — dead stock (zero sales) and slow movers.
 * Includes a discount calculator that recalculates suggested clearance
 * price and potential revenue live as the user adjusts the discount %.
 */

import React, { useState } from 'react';
import {
  Card,
  DataTable,
  SkeletonBodyText,
  Banner,
  EmptyState,
  Text,
  BlockStack,
  Badge,
  Pagination,
  Select,
  InlineStack,
  TextField,
  Button,
} from '@shopify/polaris';

import { useReport } from '../hooks/useReport';
import { useExport } from '../hooks/useExport';
import { api } from '../api/api';
import {
  formatCurrency,
  formatNumber,
  formatRate,
  periodLabel,
} from '../utils/formatters';

const DEADSTOCK_CSV_COLUMNS = [
  { key: 'productTitle', label: 'Product' },
  { key: 'sku', label: 'SKU' },
  { key: 'productType', label: 'Product Type' },
  { key: 'inventoryQuantity', label: 'Inventory Qty' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'unitsSold', label: 'Units Sold' },
  { key: 'price', label: 'Price' },
  { key: 'isDeadStock', label: 'Dead Stock' },
  { key: 'isSlowMover', label: 'Slow Mover' },
];

const ROWS_PER_PAGE = 50;

const SORT_OPTIONS = [
  { label: 'Inventory qty (high → low)', value: 'inventoryQuantity:desc' },
  { label: 'Stock value (high → low)', value: 'overstockValue:desc' },
  { label: 'Price (high → low)', value: 'price:desc' },
  { label: 'Units sold (low → high)', value: 'unitsSold:asc' },
  { label: 'Sell rate (low → high)', value: 'dailySellRate:asc' },
  { label: 'Product name A → Z', value: 'productTitle:asc' },
];

const DISCOUNT_OPTIONS = [
  { label: '10%', value: '10' },
  { label: '15%', value: '15' },
  { label: '20%', value: '20' },
  { label: '25%', value: '25' },
  { label: '30%', value: '30' },
  { label: '35%', value: '35' },
  { label: '40%', value: '40' },
  { label: '45%', value: '45' },
  { label: '50%', value: '50' },
  { label: '55%', value: '55' },
  { label: '60%', value: '60' },
  { label: '65%', value: '65' },
  { label: '70%', value: '70' },
];

/**
 * DeadStockPage — clearance alerts with discount calculator.
 *
 * @param {{ period: string, overstockDays: number, filterBar: JSX.Element }} props
 */
export function DeadStockPage({ period, overstockDays, filterBar }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('inventoryQuantity:desc');
  const [search, setSearch] = useState('');
  const [productType, setProductType] = useState('');
  const [vendor, setVendor] = useState('');
  const [discountPct, setDiscountPct] = useState('20');

  const [sortBy, sortDir] = sort.split(':');
  const discount = parseInt(discountPct, 10);

  const { data, loading, error } = useReport(api.getDeadStock, {
    period,
    overstockDays,
    page,
    limit: ROWS_PER_PAGE,
    sortBy,
    sortDir,
    search,
    productType,
    vendor,
  });

  const { data: filterOptions } = useReport(api.getFilters, { period });

  const csvFilename = `clearance-alerts-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
  const { exporting, handleExport } = useExport(
    api.getDeadStock,
    { period, overstockDays, sortBy, sortDir, search, productType, vendor },
    DEADSTOCK_CSV_COLUMNS,
    csvFilename,
  );

  const typeOptions = [
    { label: 'All types', value: '' },
    ...(filterOptions?.productTypes?.map((t) => ({ label: t, value: t })) || []),
  ];

  const vendorOptions = [
    { label: 'All vendors', value: '' },
    ...(filterOptions?.vendors?.map((v) => ({ label: v, value: v })) || []),
  ];

  const rows = (data?.data || []).map((row) => {
    const suggestedPrice = Math.round(row.price * (1 - discount / 100) * 100) / 100;
    const potentialRevenue = Math.round(suggestedPrice * row.inventoryQuantity * 100) / 100;

    return [
      <BlockStack gap="050">
        <Text variant="bodyMd" fontWeight="semibold" as="span">{row.productTitle}</Text>
        <Text variant="bodySm" tone="subdued" as="span">{row.sku} · {row.variantTitle}</Text>
      </BlockStack>,
      row.productType,
      formatNumber(row.inventoryQuantity),
      formatCurrency(row.revenue),
      formatCurrency(row.price),
      formatCurrency(suggestedPrice),
      <Text fontWeight="semibold" as="span">{formatCurrency(potentialRevenue)}</Text>,
      formatRate(row.dailySellRate),
      row.isDeadStock
        ? <Badge tone="critical">Dead Stock</Badge>
        : <Badge tone="attention">Slow Mover</Badge>,
    ];
  });

  // Totals for the summary line
  const totalStockValue = (data?.data || []).reduce(
    (sum, r) => sum + r.inventoryQuantity * r.price,
    0,
  );
  const totalPotentialRevenue = (data?.data || []).reduce(
    (sum, r) => {
      const sp = Math.round(r.price * (1 - discount / 100) * 100) / 100;
      return sum + sp * r.inventoryQuantity;
    },
    0,
  );

  const hasData = !loading && !error && rows.length > 0;
  const isEmpty = !loading && !error && rows.length === 0;

  return (
    <BlockStack gap="400">
      <Card>{filterBar}</Card>

      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Clearance Alerts — {periodLabel(period)}
            </Text>
            <InlineStack gap="300" blockAlign="center">
              {data?.pagination && (
                <Text variant="bodySm" tone="subdued" as="span">
                  {formatNumber(data.pagination.total)} SKUs
                </Text>
              )}
              <Button
                size="slim"
                onClick={handleExport}
                loading={exporting}
                disabled={loading || !data?.data?.length}
              >
                Export CSV
              </Button>
            </InlineStack>
          </InlineStack>

          {/* Search + filters */}
          <InlineStack gap="300" wrap>
            <div style={{ flex: 1, minWidth: 220 }}>
              <TextField
                label="" labelHidden
                placeholder="Search product or SKU…"
                value={search}
                onChange={(v) => { setSearch(v); setPage(1); }}
                clearButton
                onClearButtonClick={() => { setSearch(''); setPage(1); }}
                autoComplete="off"
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <Select
                label="" labelHidden
                options={typeOptions}
                value={productType}
                onChange={(v) => { setProductType(v); setPage(1); }}
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <Select
                label="" labelHidden
                options={vendorOptions}
                value={vendor}
                onChange={(v) => { setVendor(v); setPage(1); }}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <Select
                label="" labelHidden
                options={SORT_OPTIONS}
                value={sort}
                onChange={(v) => { setSort(v); setPage(1); }}
              />
            </div>
          </InlineStack>

          {/* Discount calculator */}
          <InlineStack gap="300" blockAlign="center">
            <Text variant="bodyMd" as="span" tone="subdued">Discount:</Text>
            <div style={{ minWidth: 100 }}>
              <Select
                label="" labelHidden
                options={DISCOUNT_OPTIONS}
                value={discountPct}
                onChange={setDiscountPct}
              />
            </div>
            {hasData && (
              <Text variant="bodySm" as="span" tone="subdued">
                Stock value: {formatCurrency(totalStockValue)} →
                Potential at {discount}% off: {formatCurrency(totalPotentialRevenue)}
              </Text>
            )}
          </InlineStack>

          {error && (
            <Banner title="Error loading clearance data" tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          {loading && <SkeletonBodyText lines={12} />}

          {isEmpty && (
            <EmptyState
              heading="No clearance candidates found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>
                No clearance candidates found for this period. Try a longer
                time range or lower the overstock threshold.
              </p>
            </EmptyState>
          )}

          {hasData && (
            <>
              <DataTable
                columnContentTypes={[
                  'text', 'text', 'numeric', 'numeric', 'numeric',
                  'numeric', 'numeric', 'text', 'text',
                ]}
                headings={[
                  'Product / SKU', 'Type', 'In Stock', 'Revenue',
                  'Price', `At ${discount}% Off`, 'Potential Rev.',
                  'Sell Rate', 'Status',
                ]}
                rows={rows}
                footerContent={`Page ${data?.pagination?.page} of ${data?.pagination?.totalPages}`}
              />
              <Pagination
                hasPrevious={page > 1}
                hasNext={page < (data?.pagination?.totalPages || 1)}
                onPrevious={() => setPage((p) => p - 1)}
                onNext={() => setPage((p) => p + 1)}
                label={`Page ${page} of ${data?.pagination?.totalPages || 1}`}
              />
            </>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
